import { useDispatch, useSelector } from "react-redux";
import { GlobalState } from "../../Redux/RootReducer";
import { useEffect, useState } from "react";
import { IBookTimeline } from "../../Models/IBookTimeline";
import { IEvent } from "../../Models/IEvent";
import { IAPIResponse } from "../../Services/AjaxService";
import { ToastMessage } from "../../Utils/UIMessages";
import Tr from "../../Utils/Translations/Translations";
import { GenericActions } from "../../Redux/Generic/GenericAction";
import Loadable from "../Loadable/Loadable";
import MultiForm from "../MultiForm/MultiForm";
import ApiService from "../../Services/ApiService";
import { Label, MessageBar, MessageBarType } from "@fluentui/react";
import { IMonthMap } from "../../Components/MonthMapEditor/MonthMapEditor";
import {
  CustomDateISOFormat,
  CustomISODateCompare,
} from "../../Utils/EventProcessing";

// --- CUSTOM DATE LOGIC ---
const addDaysToCustomDate = (
  currentDate: string,
  daysToAdd: number,
  monthMap: IMonthMap,
): string => {
  if (!monthMap || !monthMap.Months || monthMap.Months.length === 0)
    return currentDate;

  let [yearStr, monthName, dayStr] = currentDate.split("/");
  let currentYear = parseInt(yearStr);
  let currentDay = parseInt(dayStr);

  let currentMonthIndex = monthMap.Months.findIndex(
    (m) => m.MonthName === monthName,
  );
  if (currentMonthIndex === -1) return currentDate;

  currentDay += daysToAdd;

  while (true) {
    const currentMonthObj = monthMap.Months[currentMonthIndex];
    let maxDaysInMonth = currentMonthObj.MonthMaxDays;

    if (
      currentMonthObj.LeapYearEveryTotYears !== 0 &&
      currentYear % currentMonthObj.LeapYearEveryTotYears === 0
    ) {
      maxDaysInMonth += 1;
    }

    if (currentDay > maxDaysInMonth) {
      currentDay -= maxDaysInMonth;
      currentMonthIndex++;

      if (currentMonthIndex >= monthMap.Months.length) {
        currentMonthIndex = 0;
        currentYear++;
      }
    } else {
      break;
    }
  }

  while (currentDay <= 0) {
    currentMonthIndex--;
    if (currentMonthIndex < 0) {
      currentMonthIndex = monthMap.Months.length - 1;
      currentYear--;
    }

    const currentMonthObj = monthMap.Months[currentMonthIndex];
    let maxDaysInMonth = currentMonthObj.MonthMaxDays;
    if (
      currentMonthObj.LeapYearEveryTotYears !== 0 &&
      currentYear % currentMonthObj.LeapYearEveryTotYears === 0
    ) {
      maxDaysInMonth += 1;
    }

    currentDay += maxDaysInMonth;
  }

  const newMonthName = monthMap.Months[currentMonthIndex].MonthName;
  return `${currentYear}/${newMonthName}/${currentDay}`;
};

export interface IEventPushForwardProps {
  onDismiss: () => void;
}

const EventPushForward = (props: IEventPushForwardProps) => {
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState<boolean>(false);
  const [affectedEvents, setAffectedEvents] = useState<IEvent[]>([]);

  const eventsDate: string = useSelector(
    (state: GlobalState) => state.generic.exploreEventsDate,
  );
  const globalEvents: IEvent[] = useSelector(
    (state: GlobalState) => state.generic.globalevents,
  );
  const activeTimeline: IBookTimeline | undefined = useSelector(
    (state: GlobalState) => state.generic.activeTimeline,
  );
  const activeBookMonthMapStr: string | undefined = useSelector(
    (state: GlobalState) => state.generic.activeBook?.MonthMap,
  );

  useEffect(() => {
    if (!activeTimeline || !eventsDate || !activeBookMonthMapStr) return;

    const monthMap: IMonthMap = JSON.parse(activeBookMonthMapStr);
    const timelineEvents = globalEvents.filter(
      (x: IEvent) => x.TimelineId === activeTimeline.Id,
    );

    const targetDateISO = CustomDateISOFormat(eventsDate, monthMap);
    const futureEvents = timelineEvents.filter((x: IEvent) => {
      const eventISO = CustomDateISOFormat(x.CustomDate, monthMap);
      return CustomISODateCompare(eventISO, targetDateISO) >= 0;
    });

    setAffectedEvents(futureEvents);
  }, [eventsDate, globalEvents, activeTimeline, activeBookMonthMapStr]);

  const executePushForward = async (daysToPush: number) => {
    if (!activeBookMonthMapStr) return;
    const monthMap: IMonthMap = JSON.parse(activeBookMonthMapStr);

    setProcessing(true);

    const eventsToPush = affectedEvents.map((event) => {
      const newDate = addDaysToCustomDate(
        event.CustomDate,
        daysToPush,
        monthMap,
      );
      return {
        id: event.Id!,
        CustomDate: newDate,
      };
    });

    try {
      const response: IAPIResponse =
        await ApiService.EventController.PushEventsDates(eventsToPush);

      if (response.error !== null) {
        ToastMessage("error", Tr.Translate("system", "generic_api_failure"));
      } else {
        ToastMessage(
          "success",
          Tr.Translate("language", "action_completed_successfully"),
        );

        const updateLookup = new Map(
          eventsToPush.map((e) => [e.id, e.CustomDate]),
        );

        const newGlobalEvents: IEvent[] = globalEvents.map((ev) => {
          const eventId = ev.Id!;
          if (updateLookup.has(eventId)) {
            return { ...ev, CustomDate: updateLookup.get(eventId)! };
          }
          return ev;
        });

        dispatch(GenericActions.setEvents(newGlobalEvents));
        props.onDismiss();
      }
    } catch (err) {
      ToastMessage("error", Tr.Translate("system", "generic_api_failure"));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <Loadable
        isLoading={processing}
        content={
          <div className="event-push-forward-container">
            <MessageBar
              messageBarType={MessageBarType.warning}
              styles={{ root: { marginBottom: 15 } }}
            >
              {Tr.Translate("language", "warning_push_forward_explanation")}
            </MessageBar>

            <Label>
              {Tr.Translate("language", "events_found_from_date") +
                ": " +
                affectedEvents.length}
            </Label>

            <MultiForm
              autoButton={Tr.Translate("language", "push_events")}
              autoButtonIcon="FastForward"
              formUniqueId="pushEventsForwardForm"
              onSubmit={(data: any) => {
                const days = parseInt(data.days, 10);
                if (isNaN(days) || days === 0) {
                  ToastMessage(
                    "warning",
                    Tr.Translate("language", "invalid_days_input"),
                  );
                  return;
                }
                executePushForward(days);
              }}
              inputs={[
                {
                  type: "number",
                  name: "days",
                  currentValue: "1",
                  required: true,
                  label: Tr.Translate("language", "days_to_push_forward_label"),
                  extraParams: {
                    step: 1,
                    // Added the hint here as an underElement
                    underElement: (
                      <span
                        style={{
                          fontSize: "0.85em",
                          color: "#666",
                          fontStyle: "italic",
                        }}
                      >
                        {Tr.Translate(
                          "language",
                          "negative_numbers_to_pull_back",
                        )}
                      </span>
                    ),
                  },
                },
              ]}
            />
          </div>
        }
      />
    </div>
  );
};

export default EventPushForward;
